import HeroScrollCue from "./HeroScrollCue.web";
import UseCaseFilm from "./UseCaseFilm.web";
import React, { useState } from "react";
import { Link } from "expo-router";
import MetaHead from "@/components/MetaHead";
import PublicFooter from "@/components/PublicFooter";
import BasedBenchmarkCompass from "./BasedBenchmarkCompass.web";
import films from "@/constants/useCaseFilms.json";
import churchCopy from "@/constants/churchesContent.json";
import results from "@/constants/basedBenchmarkCompass.json";
import { FREE_USER_STARTING_CREDITS_DISPLAY } from "@/constants/config";
import { businessCopy } from "@/constants/businessContent";
import useBusinessEnrollment from "@/components/Business/useBusinessEnrollment";
import "@/app/styles/based-compass.css";
import "./ChurchesPage.css";

const MINISTRY_WORK = [
  [
    "01",
    "Sunday, prepared.",
    "Turn your message and verified event details into announcements, a bulletin, a newsletter, and small-group discussion guides.",
    "Announcements · Newsletters · Small groups",
  ],
  [
    "02",
    "Your message, beautifully made.",
    "Create sermon graphics, worship backgrounds, outreach videos, and music with the creative tools in Gab AI.",
    "Images · Video · Music",
  ],
  [
    "03",
    "The week, organized.",
    "Draft volunteer roles, event run sheets, outreach plans, and donor thank-you notes. Give your team a useful first draft to review.",
    "Volunteers · Events · Donor communications",
  ],
];
const FAQS = churchCopy.faqs.map(({ q, a }) => [
  q,
  a
    .replace("{{QUESTIONS}}", results.questionCount)
    .replace("{{TEST_DATE}}", results.completedAt.slice(0, 10)),
]);

function Cta({ href, children, secondary = false }) {
  return (
    <Link
      href={href}
      className={`church-cta${secondary ? " church-cta-secondary" : ""}`}
    >
      {children}
      <span aria-hidden="true">↗</span>
    </Link>
  );
}

export default function ChurchesPage() {
  const [selectedId, setSelectedId] = useState(results.targets[0].id);
  const enrollment = useBusinessEnrollment();
  const business = businessCopy(enrollment);
  const organizationHref = business.primary?.href || "/business";
  const organizationLabel = business.primary
    ? "Set up your ministry"
    : "Explore organization plans";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: "Gab AI for Churches & Ministries",
        url: "https://gab.ai/use-cases/churches",
        description:
          "Christian and conservative AI for your ministry, with creative tools, shared credits, and organization controls.",
      },
      {
        "@type": "VideoObject",
        name: "Gab AI for Churches & Ministries",
        description: films.churches.description,
        thumbnailUrl: "https://gab.ai/churches/ministry-film.jpg",
        contentUrl: "https://gab.ai/churches/ministry-film.mp4",
        uploadDate: films.churches.uploadDate,
        duration: "PT25S",
      },
      {
        "@type": "ItemList",
        name: "AI tools for churches and ministries",
        itemListElement: churchCopy.tools.map((tool, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: tool.name,
          description: tool.desc,
        })),
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQS.map(([q, a]) => ({
          "@type": "Question",
          name: q,
          acceptedAnswer: { "@type": "Answer", text: a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: ["Home", "Use cases", "Churches & ministries"].map(
          (name, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name,
            item: [
              "https://gab.ai",
              "https://gab.ai/use-cases",
              "https://gab.ai/use-cases/churches",
            ][i],
          }),
        ),
      },
    ],
  };
  return (
    <div className="church-page">
      <MetaHead
        title={churchCopy.seo.title}
        description={churchCopy.seo.description}
        canonical="/use-cases/churches"
        image="https://gab.ai/churches/ministry-film.jpg"
        structuredData={schema}
      />
      <main
        id="main-content"
        className="church-scroll"
        data-landing-scroll="true"
      >
        <div className="church-content">
          <header className="church-hero">
            <p className="church-eyebrow">
              Gab AI for churches &amp; ministries
            </p>
            <h1>
              More time for
              <br />
              <span>your mission.</span>
            </h1>
            <p className="church-lede">
              AI that shares your values.
              <br />A platform for your whole ministry.
            </p>
            <p className="church-intro">
              Meet Gab AI, our in-house AI model trained on Christian and
              conservative values. Prepare for Sunday, create for your
              community, and bring your team together with Gab AI for Business.
            </p>
            <div className="church-actions">
              <Cta href={organizationHref}>{organizationLabel}</Cta>
              <Cta href="/signup" secondary>
                Try Gab AI free
              </Cta>
            </div>
            <p className="church-fine">
              {FREE_USER_STARTING_CREDITS_DISPLAY} free credits · No credit card
              required to try Gab AI
            </p>
            <HeroScrollCue href="#church-values-title" />
            <figure className="church-film">
              <UseCaseFilm
                src="/churches/ministry-film.mp4"
                poster="/churches/ministry-film.jpg"
                label="Gab AI for churches and ministries: Christian AI, ministry tools, and organization controls"
              >
                <track
                  kind="captions"
                  src="/churches/ministry-film.vtt"
                  srcLang="en"
                  label="English"
                />
                <p>
                  <a href="/churches/ministry-film.mp4">
                    Download the ministry overview video
                  </a>
                </p>
              </UseCaseFilm>
              <figcaption>
                <span>Your values. Your people. Your mission.</span>
                <span>
                  A 25-second narrated introduction · English captions
                </span>
              </figcaption>
            </figure>
          </header>
          <section
            className="church-section church-values"
            aria-labelledby="church-values-title"
          >
            <div>
              <p className="church-eyebrow">Built with conviction</p>
              <h2 id="church-values-title">
                Your faith isn’t
                <br />
                an afterthought.
              </h2>
            </div>
            <div className="church-values-copy">
              <img
                src="/brand-assets/gab-ai-mark-black.svg"
                alt=""
                width="44"
                height="44"
              />
              <h3>Meet Gab AI.</h3>
              <p>
                We built our own AI model in-house and trained it on Christian
                and conservative values. For churches and ministries, that means
                a starting point that respects your faith, your convictions, and
                the people you serve.
              </p>
              <p>
                Use Gab AI for a Christian perspective. Choose other models when
                you want to compare answers. Your team stays in control of the
                final word.
              </p>
              <Link href="/right-wing-ai" className="church-text-link">
                Get to know Gab AI <span aria-hidden="true">→</span>
              </Link>
            </div>
          </section>
          <section
            className="church-section church-benchmark"
            aria-labelledby="church-benchmark-title"
          >
            <div className="church-benchmark-copy">
              <p className="church-eyebrow">The difference, measured</p>
              <h2 id="church-benchmark-title">
                Gab AI on the right.
                <br />
                The rest on the left.
              </h2>
              <p>
                Every other model in this published comparison landed on the
                economic left. Gab AI landed on the economic right.
              </p>
              <div className="church-test-record">
                <strong>{results.targets.length} models</strong>
                <span>{results.questionCount} questions each</span>
                <span>Tested {results.completedAt.slice(0, 10)}</span>
              </div>
              <p className="church-fine">
                Gab AI ran this test. Gab AI includes Gab’s product prompting;
                other models used provider defaults without an added political
                prompt. Economic position is the horizontal axis. Authority and
                liberty are the vertical axis.
              </p>
              <Link
                href="/based-benchmark#methodology"
                className="church-text-link"
              >
                Read the results &amp; methodology{" "}
                <span aria-hidden="true">→</span>
              </Link>
            </div>
            <BasedBenchmarkCompass
              targets={results.targets.map((target) => ({
                ...target,
                label: target.label.replace(/Gab Arya|Arya/g, "Gab AI"),
              }))}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </section>
          <section
            className="church-section"
            aria-labelledby="church-work-title"
          >
            <div className="church-section-head">
              <p className="church-eyebrow">From Sunday to every day</p>
              <h2 id="church-work-title">
                A small team.
                <br />A lot more possible.
              </h2>
              <p>
                Give your people a head start on the work around the ministry.
              </p>
            </div>
            <div className="church-work-grid">
              {MINISTRY_WORK.map(([number, title, body, tags]) => (
                <article key={number}>
                  <span className="church-number">{number}</span>
                  <h3>{title}</h3>
                  <p>{body}</p>
                  <p className="church-work-tags">{tags}</p>
                </article>
              ))}
            </div>
          </section>
          <section
            className="church-section"
            aria-labelledby="church-library-title"
          >
            <div className="church-section-head">
              <p className="church-eyebrow">Made for ministry</p>
              <h2 id="church-library-title">
                Start with something
                <br />
                useful.
              </h2>
              <p>
                Explore your ministry tools, then open a prompt and make it your
                own.
              </p>
            </div>
            <div className="church-library-tools">
              {churchCopy.tools.map((tool) => (
                <article key={tool.name}>
                  <h3>{tool.name}</h3>
                  <p>{tool.desc}</p>
                </article>
              ))}
            </div>
            <div className="church-faq church-library-prompts">
              {churchCopy.examplePrompts.map((prompt) => (
                <details key={prompt.title}>
                  <summary>
                    {prompt.title}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{prompt.prompt}</p>
                  <Link href="/signup">Try this with Gab AI ↗</Link>
                </details>
              ))}
            </div>
          </section>
          <section
            id="organization"
            className="church-section church-organization"
            aria-labelledby="church-org-title"
          >
            <div className="church-section-head">
              <p className="church-eyebrow">
                Gab AI for Business · For your ministry
              </p>
              <h2 id="church-org-title">
                One church.
                <br />
                One connected team.
              </h2>
              <p>
                Bring your pastors, staff, and ministry leaders together as one
                organization. Give each person their own account, with billing
                and resources managed centrally.
              </p>
            </div>
            <div className="church-org-layout">
              <div
                className="church-org-preview"
                aria-label="Illustrative ministry organization"
              >
                <div className="church-preview-top">
                  <img
                    src="/brand-assets/gab-ai-mark-black.svg"
                    alt=""
                    width="28"
                    height="28"
                  />
                  <span>Ministry organization</span>
                  <span className="church-demo-label">Illustration</span>
                </div>
                <div className="church-pool">
                  <span>One shared credit pool</span>
                  <strong>
                    Resources for
                    <br />
                    the whole team.
                  </strong>
                  <div className="church-pool-bar" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </div>
                  <small>Included seat credits, together.</small>
                </div>
                {[
                  ["Pastor", "Owner"],
                  ["Church office", "Admin"],
                  ["Communications", "Member"],
                ].map(([name, role]) => (
                  <div className="church-member" key={name}>
                    <span className="church-avatar" aria-hidden="true">
                      {name[0]}
                    </span>
                    <span>{name}</span>
                    <span>{role}</span>
                  </div>
                ))}
              </div>
              <div className="church-org-features">
                {[
                  [
                    "Centralized billing",
                    "Manage paid team seats on one organization bill. Existing owner subscriptions may keep separate billing; setup shows the details before payment.",
                  ],
                  [
                    "Administrative control",
                    "Invite or remove members, assign Plus or Plus Premium seats, view member usage, and set spending limits. Owners and admins have distinct roles.",
                  ],
                  [
                    "A shared credit pool",
                    "Every paid seat contributes its included credits to the organization. Your team draws from one balance, with visibility into usage.",
                  ],
                  [
                    "Individual accounts. Private work.",
                    "Your people use their own Gab AI accounts. Their private conversations and files stay private; administrators manage access and spending.",
                  ],
                ].map(([title, body]) => (
                  <article key={title}>
                    <h3>{title}</h3>
                    <p>{body}</p>
                  </article>
                ))}
              </div>
            </div>
            <div className="church-org-bottom">
              <div>
                <strong>Start with 2 seats. Grow with your ministry.</strong>
                <p>
                  Mix Plus and Plus Premium at their individual monthly prices.
                  Teams of 2–250.
                </p>
                {enrollment === "soon" && (
                  <p>
                    Organization enrollment is coming soon. Explore the plans
                    and setup guide.
                  </p>
                )}
              </div>
              <Cta href={organizationHref}>{organizationLabel}</Cta>
            </div>
            <Link href="/docs/business" className="church-text-link">
              Read the organization guide <span aria-hidden="true">→</span>
            </Link>
          </section>
          <section
            className="church-section church-faq"
            aria-labelledby="church-faq-title"
          >
            <div>
              <p className="church-eyebrow">A few good questions</p>
              <h2 id="church-faq-title">Before you begin.</h2>
            </div>
            <div>
              {FAQS.map(([q, a]) => (
                <details key={q}>
                  <summary>
                    {q}
                    <span aria-hidden="true">+</span>
                  </summary>
                  <p>{a}</p>
                </details>
              ))}
            </div>
          </section>
          <nav className="church-related" aria-label="Related use cases">
            <p className="church-eyebrow">Keep exploring</p>
            {churchCopy.relatedUseCases.map((item) => (
              <Link key={item.slug} href={`/use-cases/${item.slug}`}>
                {item.name} <span aria-hidden="true">↗</span>
              </Link>
            ))}
          </nav>
          <section className="church-close">
            <p className="church-eyebrow">
              Technology that serves your mission
            </p>
            <h2>
              Equip your people.
              <br />
              Keep your purpose.
            </h2>
            <p>
              Christian and conservative AI. Creative tools. One place for your
              ministry to work.
            </p>
            <div className="church-actions">
              <Cta href={organizationHref}>{organizationLabel}</Cta>
              <Cta href="/signup" secondary>
                Try Gab AI free
              </Cta>
            </div>
          </section>
        </div>
        <PublicFooter variant="editorial" showHero={false} />
      </main>
    </div>
  );
}
